import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { SEOHead, articleJsonLd, breadcrumbJsonLd } from "@/components/seo/SEOHead.tsx";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

/**
 * Hardcoded seed content for the blog post page.
 * In Phase 2 this will be fetched from GET /cms/pages/:slug.
 */
export const SEED_POSTS: Record<
  string,
  { title: string; excerpt: string; category: string; date: string; readTime: string; body: string }
> = {
  "what-is-a-faceless-youtube-channel": {
    title: "What Is a Faceless YouTube Channel? (And How to Start One in 2026)",
    excerpt: "Faceless channels are booming. Here's what they are, why they work, and the exact steps to launch one without ever turning on a camera.",
    category: "Getting Started",
    date: "2026-04-15",
    readTime: "7 min read",
    body: `A faceless YouTube channel is exactly what it sounds like — a channel where the creator never appears on camera. Instead, the content relies on narration, imagery (stock footage, AI-generated images, screen recordings), captions, and music to deliver value to viewers.

## Why faceless channels are growing

The appeal is obvious: no studio, no camera, no lighting, no editing yourself out of bad takes. But beyond convenience, faceless channels solve a real problem — they let creators publish consistently without the bottleneck of on-camera production.

Some of the biggest YouTube channels in education, history, finance, and motivation are faceless. Channels like Kurzgesagt, ColdFusion, and Economics Explained have proven that narrated visual content can attract millions of subscribers.

## What makes a good faceless niche?

The best faceless niches share three traits:

1. **The topic is inherently visual** — history, science, geography, and travel all lend themselves to narrated image sequences.
2. **The audience wants information, not personality** — viewers come for the content, not the creator's face.
3. **There's enough depth for consistent publishing** — niches like "ancient civilizations" or "personal finance" have endless sub-topics.

## How TubeForge fits in

TubeForge automates the production pipeline for faceless channels. You provide the topic, the AI generates a script, illustrates each scene, records voiceover, aligns captions, and renders the final video. You review and approve at two gates — script and storyboard — before anything is published.

The result: a full video in a few steps, with complete creative control over every word and image.

## Getting started

1. Sign up for a free account (2 free videos, no credit card)
2. Choose a channel preset that matches your niche
3. Add a topic or let the AI pick one
4. Review the script — edit any section, regenerate with a different tone
5. Review the storyboard — swap any image, adjust captions
6. Render and download your MP4

That's it. Your first faceless video, done in minutes.`,
  },
  "best-niches-for-faceless-youtube": {
    title: "12 Best Niches for Faceless YouTube Channels in 2026",
    excerpt: "History, finance, science, motivation — we break down the most profitable faceless niches with estimated CPMs and competition levels.",
    category: "Strategy",
    date: "2026-04-08",
    readTime: "9 min read",
    body: `Choosing the right niche is the single most important decision for a faceless YouTube channel. The niche determines your audience, your CPM (cost per mille — what advertisers pay per 1,000 views), and how easy it is to produce content consistently.

Here are 12 niches that work exceptionally well for faceless, narrated video content.

## 1. History & Documentary

**CPM range:** $8–$18 · **Competition:** Medium · **Format:** Long-form

History content is evergreen. Topics like ancient civilizations, famous battles, and historical mysteries attract viewers who watch long videos — great for ad revenue. The visual format (maps, paintings, artifacts) is perfect for AI-generated imagery.

## 2. Personal Finance & Investing

**CPM range:** $15–$35 · **Competition:** High · **Format:** Long-form

Finance has some of the highest CPMs on YouTube because financial advertisers pay premium rates. The challenge is competition — but sub-niches like "finance for millennials" or "crypto explained simply" still have room.

## 3. Science & Education

**CPM range:** $6–$14 · **Competition:** Medium · **Format:** Long-form + Shorts

Science explainers perform well in both long-form and Shorts. Topics like "why the sky is blue" or "how black holes work" are perennial search traffic drivers.

## 4. Motivation & Self-Help

**CPM range:** $4–$10 · **Competition:** High · **Format:** Shorts + Long-form

Motivational content is one of the easiest faceless niches to start with. The scripts are formulaic, the imagery is aspirational, and the audience engages heavily with Shorts.

## 5. Book Summaries

**CPM range:** $6–$12 · **Competition:** Medium · **Format:** Long-form

Summarize popular non-fiction books in 5–10 minute videos. The two-gate workflow in TubeForge is especially useful here — you can fact-check the AI's summary before any visuals are generated.

## 6. Geography & Travel

**CPM range:** $5–$12 · **Competition:** Low-Medium · **Format:** Long-form

"Top 10 places" and country profile videos consistently perform well. AI image generation handles location-style imagery effectively.

## 7. Tech Explainers

**CPM range:** $8–$20 · **Competition:** Medium · **Format:** Shorts + Long-form

How technologies work, product comparisons, and industry news. Tech Shorts (60-second explainers) are underserved and growing fast.

## 8. Health & Wellness

**CPM range:** $6–$15 · **Competition:** Medium · **Format:** Long-form

"What happens to your body when…" videos drive massive search traffic. Be careful with medical claims — stick to well-sourced, educational content.

## 9. True Crime & Mysteries

**CPM range:** $5–$10 · **Competition:** High · **Format:** Long-form

True crime and unsolved mystery content has a huge dedicated audience. The script review gate is important here — accuracy matters.

## 10. Music History & Pop Culture

**CPM range:** $4–$8 · **Competition:** Low-Medium · **Format:** Long-form

Artist biographies, genre histories, and "the story behind the song" videos. Lower CPMs but very loyal audiences.

## 11. Food Science & Cuisine History

**CPM range:** $5–$10 · **Competition:** Low · **Format:** Shorts + Long-form

Not cooking tutorials — think "why does cutting onions make you cry?" or "the history of pizza." Educational food content is underserved.

## 12. Space & Astronomy

**CPM range:** $6–$14 · **Competition:** Medium · **Format:** Long-form

Space content is visually stunning and endlessly fascinating. AI-generated space imagery works surprisingly well for narrated explainers.

## Picking your niche

Start with a niche you can talk about for 100+ videos. If you run out of topics after 10, you've niched too narrowly. TubeForge's preset system lets you save your niche settings so every new video starts with the right defaults.`,
  },

  "ai-video-generation-workflow": {
    title: "The Complete AI Video Generation Workflow: Script → Storyboard → Publish",
    excerpt: "A step-by-step walkthrough of how AI-powered tools like TubeForge turn a topic into a publish-ready video in a few steps.",
    category: "Product",
    date: "2026-03-28",
    readTime: "6 min read",
    body: `Creating a YouTube video used to mean hours of work: research, scripting, recording, editing, adding captions, exporting, uploading. AI video generation collapses that pipeline into minutes. Here's exactly how the workflow works — from raw topic to finished MP4.

## The old way vs the new way

Before AI video tools, a solo creator's pipeline looked like this:

1. Research and outline (1–2 hours)
2. Write and rewrite script (1–2 hours)
3. Record voiceover or on-camera footage (30–60 min)
4. Find or create visuals (30–60 min)
5. Edit in a video editor (1–2 hours)
6. Add captions manually or via a service (30 min)
7. Export and upload (15–30 min)

Total: **5–9 hours per video**, assuming no retakes and no creative blocks.

With an AI-first workflow like TubeForge, the same output takes a few steps of active work. The rest is the AI rendering in the background while you do something else.

## Stage 1 — Script generation

The workflow starts with a content profile: your channel niche, target audience, content goal, and a platform preference (YouTube long-form, Shorts, or general). You can optionally add a specific topic or angle, or leave it blank and let the AI pick one based on your niche.

The AI generates a structured script in about 30 seconds. Every script has the same three-part structure:

- **Hook** — the opening line designed to stop the scroll and create a reason to keep watching
- **Body** — the main content, divided into sections that will each become a visual scene
- **Call-to-action** — the closing instruction (subscribe, visit a link, comment, etc.)

This structure matters for SEO and retention. YouTube's algorithm measures audience retention, and a clear hook → body → CTA structure consistently outperforms rambling intros.

Once the script is generated, you read it, edit any line you want, and can regenerate with a different tone — punchier, longer, shorter, more casual, or more serious. Nothing moves forward until you click Approve.

## Stage 2 — Storyboard generation

After script approval, the AI generates one image per script segment. On a standard 5-minute video, that's typically 8–12 images.

Each image is generated from a prompt that the AI writes based on the text of that script segment. You see the prompts and can edit them. Click any frame to open an edit panel, change the prompt, and regenerate. Regeneration costs 5 credits per frame.

The storyboard stage is where the visual identity of the video is established. Choose your caption font and colour before approving — these settings apply globally to all captions in the final video.

Again, nothing renders until you click Approve Storyboard.

## Stage 3 — Automated rendering

This is where the magic happens with zero manual work:

1. **Clips** — each storyboard image is turned into a short clip, timed to its script segment
2. **Voiceover** — the AI reads the script aloud, synced to each clip
3. **Word-level captions** — Whisper transcribes the voiceover and aligns each word precisely to the audio
4. **Music mix** — background music is layered in at a level below the voice

Rendering takes 2–5 minutes for a typical 5-minute video. You watch the progress live — each stage updates in real time.

## Stage 4 — Publish or download

Once rendering completes, the video is ready. Two options:

- **Download the MP4** — available on every plan. Take the file and upload it anywhere.
- **Publish to YouTube** — fill in a title, description, and tags in a single modal. The video uploads directly to your connected YouTube channel, with the AI-disclosure label set automatically. Pro and Agency users can schedule the publish time instead of going live immediately.

## Why the two-gate model matters

The distinguishing feature of this workflow is the two mandatory review stages — script approval and storyboard approval. Many AI video tools skip one or both, generating a final video directly from a prompt.

The two-gate model means:

- You catch factual errors or off-brand language before any images are generated
- You catch visual mismatches before any audio is recorded
- The final video reflects your creative direction, not just an algorithm's best guess

This is particularly important for educational or informational channels where accuracy matters. A single factual error in an auto-generated video can damage a channel's credibility.

## How long does it actually take?

Here's a realistic time breakdown for a 5-minute video:

| Stage | AI time | Your time |
|---|---|---|
| Script generation | 30 seconds | 2–5 min (review + edits) |
| Storyboard generation | 90 seconds | 3–8 min (review + regen if needed) |
| Rendering | 3–5 minutes | 0 min (background) |
| Publish | — | 2–3 min (fill in metadata) |

**Total your time: 7–16 minutes.** The AI handles everything else.

## Getting started

If you haven't created a TubeForge account yet, the free plan includes 2 videos — no credit card required. The fastest way to understand the workflow is to just do it once. Pick a topic you know well, approve the first script the AI generates, approve the first storyboard, and watch the render complete.`,
  },

  "youtube-shorts-vs-long-form": {
    title: "YouTube Shorts vs Long-Form: Which Should You Focus On?",
    excerpt: "Shorts drive subscribers; long-form drives revenue. We look at the data and help you decide the right mix for your faceless channel.",
    category: "Strategy",
    date: "2026-03-20",
    readTime: "5 min read",
    body: `There's a real tension in YouTube content strategy right now: Shorts are getting billions of views but paying almost nothing, while long-form videos earn serious ad revenue but are harder and more expensive to produce. For faceless channel creators, the question is which format to prioritise — and the honest answer is: it depends on where you are.

## The core difference in how each format performs

**YouTube Shorts** are vertical videos under 60 seconds. They show up in a dedicated Shorts feed, are algorithmically distributed to non-subscribers, and can generate massive view counts quickly. A Shorts channel can go from 0 to 10,000 subscribers faster than almost any other format on the platform.

The catch: Shorts RPM (revenue per mille — earnings per 1,000 views) is dramatically lower than long-form. Most Shorts creators earn $0.03–$0.08 per 1,000 views. Long-form creators in the same niche earn $3–$25+ per 1,000 views. That's a 100x difference.

**Long-form videos** (typically 8–20 minutes) earn the bulk of YouTube ad revenue. They're harder to grow with — the algorithm distributes them primarily to subscribers and search results, not to cold audiences. But they compound: a video ranking in YouTube search continues driving views and revenue for years.

## What the data says for faceless channels

For faceless narrated channels specifically, long-form outperforms Shorts on nearly every revenue metric. Here's why:

**Faceless niches attract high-CPM advertisers.** Finance, education, and history content — the most popular faceless niches — attract advertisers paying $10–35 CPM. These advertisers buy long-form ad slots, not Shorts.

**Watch time is a long-form advantage.** YouTube's algorithm rewards watch time and session watch time (keeping viewers on the platform). A 12-minute video that holds 60% of viewers delivers more algorithmic reward than 40 Shorts watched for 15 seconds each.

**Search traffic is long-form territory.** Educational queries — "how does compound interest work", "what caused the Roman Empire to fall" — trigger YouTube search results that are almost exclusively long-form. Faceless educational channels live on this traffic.

## When Shorts make sense for faceless channels

Shorts aren't worthless for faceless creators — they just serve a different purpose:

**Use Shorts to grow subscribers fast.** If you're under 1,000 subscribers, Shorts can accelerate channel growth dramatically. Subscribers acquired via Shorts will see your long-form videos in their feed.

**Use Shorts as content extensions.** Take a single strong point from a long-form video and turn it into a Short. This is nearly zero additional work with AI video tools — generate a 45-second clip on the same topic, add a call-to-action to watch the full video.

**Use Shorts for trend-based topics.** Trending topics (news events, viral stories) are better suited to Shorts because speed to publish matters. For evergreen educational content, long-form wins.

## The recommended mix by channel stage

| Stage | Subscriber count | Recommended mix |
|---|---|---|
| Launch | 0–1,000 | 70% Shorts, 30% long-form. Build subscribers fast. |
| Growth | 1,000–10,000 | 50/50. Long-form starts converting Shorts subscribers into engaged viewers. |
| Established | 10,000+ | 30% Shorts, 70% long-form. Long-form is your primary revenue driver. |

## How TubeForge handles both formats

TubeForge supports both 16:9 (standard landscape, for long-form) and 9:16 (vertical, for Shorts and TikTok) video formats. Select the format when you create a new video — the AI adjusts the storyboard image dimensions and caption layout accordingly.

For faceless channels using TubeForge, the practical workflow is:

- Create one long-form video per week (your main revenue engine)
- Create one or two Shorts per week as channel-growth tools, repurposing a single concept from the long-form topic

At 2–3 videos per week, you're building both immediate subscriber growth (Shorts) and long-term revenue (long-form) simultaneously.

## The bottom line

If you're building a faceless channel for ad revenue, **long-form is non-negotiable**. Shorts alone will not generate meaningful income.

If you're building a faceless channel for audience growth, **Shorts are the fastest tool available**.

The best strategy is both — but with long-form as the primary content and Shorts as a supplementary growth layer. And for faceless AI video creators, the good news is that producing both formats from the same tool is now straightforward.`,
  },

  "ai-voiceover-comparison": {
    title: "AI Voiceover Showdown: OpenAI TTS vs ElevenLabs for YouTube",
    excerpt: "We compare the two AI voice providers available in TubeForge — quality, naturalness, language support, and when to upgrade.",
    category: "Product",
    date: "2026-03-12",
    readTime: "4 min read",
    body: `The voiceover is the soul of a faceless video. You can have perfect visuals and a flawless script, but a robotic or flat voice will drain your retention numbers. AI voice technology has improved dramatically in the last two years — but not all AI voices are equal, and the difference matters for YouTube channels.

TubeForge uses two AI voice providers across its plan tiers: OpenAI TTS on Free and Starter plans, and ElevenLabs on Pro and above. Here's an honest breakdown of both.

## OpenAI TTS — the baseline

OpenAI's Text-to-Speech (TTS) API produces voices in a range of styles: Alloy (neutral), Echo (warm male), Fable (British), Onyx (deep), Nova (female), and Shimmer (light female).

**What it does well:**

- Consistent pacing and pronunciation
- Clean, professional tone — no uncanny valley quality
- Very fast generation (a 5-minute script renders in under 10 seconds)
- Handles technical vocabulary and unusual proper nouns well
- All voices work in English at high quality

**Where it falls short:**

- Emotional range is limited — all voices have a similar measured cadence
- Doesn't vary naturally on emphasis; can sound slightly uniform on long scripts
- Language support is good but not exceptional for non-English content
- No voice cloning or custom voice creation

For most faceless YouTube channels — especially educational, informational, or explainer content — OpenAI TTS is genuinely sufficient. If you're watching a history documentary narrated by an OpenAI voice, you're unlikely to think "that sounds fake."

## ElevenLabs — the upgrade

ElevenLabs produces some of the most natural-sounding AI voices available. The gap between ElevenLabs and earlier voice generators is audible — ElevenLabs voices breathe, pause naturally, and vary emphasis in a way that feels human.

**What it does well:**

- Exceptional naturalness — the best AI voices pass casual listener tests
- Strong emotional range — voices adapt to the tone of the script content
- Excellent for content where personality matters (motivation, storytelling, documentary)
- ElevenLabs Multilingual v2 (available on Agency plan) supports 28+ languages at high quality
- Voice cloning available (not through TubeForge currently, but via the ElevenLabs platform directly)

**Where it falls short:**

- Slower generation than OpenAI — a 5-minute script may take 30–60 seconds to render
- Occasional quirks with unusual names or technical jargon
- Premium pricing — the quality costs more

## Head-to-head for YouTube use cases

| Use case | Recommended voice | Why |
|---|---|---|
| Educational explainers | OpenAI TTS (any) | Neutral, clear, doesn't distract from content |
| History documentaries | ElevenLabs | Richer tone suits narrative content |
| Finance / investing | OpenAI Onyx or ElevenLabs | Both work; ElevenLabs sounds more authoritative |
| Motivation / self-help | ElevenLabs | Emotional variance matters in this niche |
| True crime | ElevenLabs | Storytelling content benefits from natural pacing |
| Tech explainers | OpenAI TTS | Technical vocabulary handled cleanly |
| Non-English content | ElevenLabs Multilingual | Far superior language coverage |
| High-volume publishing (10+ videos/week) | OpenAI TTS | Speed advantage at scale |

## When to upgrade from OpenAI to ElevenLabs

You should stay on OpenAI TTS if:

- Your channel is educational, informational, or factual
- You're publishing in English
- You're on a high-volume schedule and render speed matters
- You're in early channel growth and the voiceover quality isn't your primary differentiator

You should upgrade to ElevenLabs (TubeForge Pro) if:

- Your niche is storytelling-heavy (true crime, history documentaries, motivation)
- Your audience expects a broadcast-quality voiceover
- You want to publish in multiple languages
- You've identified that your current voice sounds noticeably artificial compared to competitors

## The practical test

The best way to decide is to listen to both on your specific niche content. TubeForge's free plan lets you generate 2 videos with OpenAI voices. If you watch those videos and think "the voice sounds fine", stay on Starter. If you watch them and think "this sounds robotic compared to channels I'm competing with", upgrade to Pro.

Voice quality is one input into a video's success — script quality, visual quality, topic selection, and thumbnail matter as much or more. Don't let perfect be the enemy of published.`,
  },

  "how-to-write-hooks-that-keep-viewers": {
    title: "How to Write Hooks That Keep Viewers Past the First 5 Seconds",
    excerpt: "The hook is everything on YouTube. Here are 7 proven hook formulas that work especially well for faceless narrated videos.",
    category: "Writing",
    date: "2026-03-05",
    readTime: "6 min read",
    body: `YouTube's algorithm measures what happens in the first 30 seconds of your video more carefully than almost anything else. If viewers leave early, the algorithm stops recommending your content. If they stay, it pushes your video to more people.

The hook — the first line or two of your script — determines which outcome happens. For faceless narrated videos, the hook is even more critical because there's no face to create an immediate emotional connection. The words have to do all the work.

Here are 7 proven hook formulas, with examples for faceless educational channels.

## Why hooks matter more for faceless channels

On a face-cam video, the creator's presence, energy, and eye contact create an immediate psychological hook before a single word is spoken. Viewers are drawn to human faces instinctively.

Faceless videos don't have this. The first line of narration is the first impression — and it needs to do what a face does automatically: create curiosity, connection, or urgency in under 5 seconds.

## Formula 1 — The Bold Claim

State something that contradicts conventional wisdom or makes a strong assertion the viewer hasn't heard before.

**Structure:** "[Most people believe X]. They're wrong."

**Examples:**
- "Most investors think diversification protects them. It doesn't — at least not in the way they think."
- "You've been told that reading 30 minutes a day will change your life. That's not quite true — here's what the research actually says."
- "The Roman Empire didn't fall in 476 AD. That date is almost entirely a myth."

This formula works because it creates immediate cognitive dissonance. The viewer has to keep watching to resolve the tension.

## Formula 2 — The Question

Ask a question the viewer immediately wants to know the answer to. The key: make it specific, not generic.

**Structure:** "Have you ever wondered [very specific thing]?"

**Examples:**
- "Have you ever wondered why you can't tickle yourself — even though the exact same motion works when someone else does it?"
- "Have you ever wondered why banks don't just run out of money if everyone withdraws at once?"
- "Have you ever looked at the night sky and wondered whether we're actually seeing the past?"

Generic: "Have you ever wondered about the stock market?" — no one answers yes to this.
Specific: "Have you ever wondered why stock prices move up and down even when nothing changes about the company?" — this one lands.

## Formula 3 — The Surprising Statistic

Lead with a number that is genuinely unexpected. Not "90% of startups fail" (everyone knows this) — something the viewer would not have guessed.

**Structure:** "[Surprising number]. Here's why."

**Examples:**
- "The top 1% of YouTube channels get 96% of all video views. Here's what separates them from everyone else."
- "Ancient Rome had a population of over 1 million people. That number wouldn't be reached again in any European city for over 1,000 years."
- "A single teaspoon of neutron star material weighs about 10 million tonnes. Let's talk about why."

The pause after the statistic is important — give the viewer half a second to process it before moving forward.

## Formula 4 — The Story Open

Begin mid-story, in the present tense, at a moment of tension or decision. Do not introduce the topic first — drop the viewer directly into the scene.

**Structure:** "[Scene-setting sentence]. [Tension or conflict]. [Bridge to topic]."

**Examples:**
- "It's 1929. A trader in New York is watching the stock ticker. He has no idea that in the next 48 hours, he will lose everything — and trigger the worst financial crisis in history."
- "In 1945, a Japanese soldier named Hiroo Onoda was told to hold his position and wait for orders. He took those orders seriously. He didn't surrender until 1974."

Story opens are highly effective for history, true crime, and documentary-style content. They work less well for purely informational topics like finance basics or science explainers — use Formula 1 or 2 for those.

## Formula 5 — The Counterintuitive Explanation

Promise that you're going to explain why something works in a way that contradicts what the viewer assumes.

**Structure:** "The reason [common thing] works isn't what you think."

**Examples:**
- "The reason compound interest makes you rich isn't because of the interest rate. It's because of time — and most people start too late."
- "The reason habit stacking works isn't because it builds discipline. It's because it removes the need for discipline entirely."
- "The reason some YouTube channels grow to a million subscribers without ever showing a face isn't luck — it's a specific content format."

This formula is particularly well-suited for educational faceless channels because it positions your video as correcting a misconception — which creates strong watch-through motivation.

## Formula 6 — The Direct Challenge

Challenge the viewer's current knowledge or behaviour. Use sparingly — it can feel aggressive if overused.

**Structure:** "If you [common mistake], you're [consequence]."

**Examples:**
- "If you're investing in index funds without understanding expense ratios, you're quietly losing thousands of dollars over your career."
- "If you're writing YouTube scripts that start with 'Hey, welcome back to the channel', you're losing 40% of your audience in the first 10 seconds."

The direct challenge works because it creates a threat response — the viewer needs to know whether they're making this mistake.

## Formula 7 — The Open Loop

Introduce a mystery or question in the first line that can only be answered by watching to the end.

**Structure:** "At the end of this video, you'll understand why [intriguing outcome]."

**Examples:**
- "By the end of this video, you'll understand why one of history's most powerful empires collapsed — and why every empire since has made the same mistake."
- "In the next five minutes, you'll see why the investing strategy everyone recommends is optimised for fund managers, not for you."

Open loops are most effective on longer videos (10+ minutes) where retention is harder to sustain. For Shorts, stick to Formulas 1–3.

## Using AI to write better hooks

TubeForge's script generator writes hooks automatically based on your content profile and topic. But you can direct the AI toward specific hook styles.

In the guidance field when creating a new video, try:
- "Start with a bold claim that contradicts what most people think about [topic]"
- "Open with a surprising statistic, then explain it"
- "Begin mid-story, at a moment of tension"

The AI will use your guidance to shape the hook style while still handling the research and writing. Review the hook in the script approval stage — if it doesn't immediately make you want to keep reading, regenerate with a different tone modifier (Punchier is especially useful for hooks) before approving.

A strong hook doesn't save a weak video. But a weak hook kills a strong one.`,
  },
};

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? SEED_POSTS[slug] : undefined;

  if (!post) {
    return (
      <section className="py-20 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-xl px-5">
          <h1 className="text-2xl font-bold mb-4">Post not found</h1>
          <p className="text-[14px] mb-6" style={{ color: T.inkSoft }}>
            This article doesn't exist yet, or it may have been moved.
          </p>
          <Link to="/blog" className="text-[14px] font-medium" style={{ color: T.accent }}>
            ← Back to blog
          </Link>
        </div>
      </section>
    );
  }

  const siteUrl = "https://tubeforge.io";

  return (
    <>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        ogType="article"
        article={{
          publishedTime: new Date(post.date).toISOString(),
          tags: [post.category],
        }}
        jsonLd={[
          articleJsonLd({
            title: post.title,
            description: post.excerpt,
            url: `${siteUrl}/blog/${slug}`,
            image: `${siteUrl}/og-default.png`,
            publishedTime: new Date(post.date).toISOString(),
          }),
          breadcrumbJsonLd([
            { name: "Blog", url: `${siteUrl}/blog` },
            { name: post.category },
            { name: post.title, url: `${siteUrl}/blog/${slug}` },
          ]),
        ]}
      />

      {/* Header */}
      <section className="py-12 md:py-16" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[13px] font-medium mb-8 hover:opacity-80 transition-opacity"
            style={{ color: T.inkSoft }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <span
              className="text-[11px] font-semibold px-2.5 py-1 rounded"
              style={{ backgroundColor: "rgba(255,91,31,0.08)", color: T.accent }}
            >
              {post.category}
            </span>
          </div>

          <h1
            className="text-2xl md:text-4xl font-bold leading-tight mb-4"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            {post.title}
          </h1>

          <div className="flex items-center gap-5 text-[13px]" style={{ color: T.inkSoft }}>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-8 md:py-12" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <article className="prose-tubeforge">
            {renderMarkdown(post.body)}
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16" style={{ backgroundColor: T.paper2, borderTop: `1px solid ${T.rule}` }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8 text-center">
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui' }}>
            Ready to try it?
          </h2>
          <p className="text-[14px] mb-6" style={{ color: T.inkSoft }}>
            Create your first faceless video in a few steps. Free, no credit card.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 text-[14px] font-semibold px-6 py-3 rounded-xl transition-opacity hover:opacity-90"
            style={{ backgroundColor: T.accent, color: "#fff" }}
          >
            Start free trial
          </Link>
        </div>
      </section>
    </>
  );
}

/**
 * Very simple markdown-to-JSX renderer for blog content.
 * Handles: ## headings, **bold**, paragraphs, numbered lists.
 * In Phase 2, replace with a proper markdown library (react-markdown).
 */
function renderMarkdown(md: string) {
  const lines = md.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Heading h2
    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={key++}
          style={{
            fontSize: 20,
            fontWeight: 700,
            fontFamily: '"Bricolage Grotesque", "Inter", system-ui',
            letterSpacing: "-0.01em",
            marginTop: 32,
            marginBottom: 12,
            color: T.ink,
          }}
        >
          {inlineFormat(line.slice(3))}
        </h2>
      );
      i++;
      continue;
    }

    // Numbered list item
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      elements.push(
        <ol
          key={key++}
          style={{
            paddingLeft: 20,
            marginBottom: 16,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {items.map((item, idx) => (
            <li key={idx} style={{ fontSize: 15, lineHeight: 1.7, color: T.inkSoft }}>
              {inlineFormat(item)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Unordered bullet list (- item)
    if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul
          key={key++}
          style={{
            paddingLeft: 20,
            marginBottom: 16,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {items.map((item, idx) => (
            <li key={idx} style={{ fontSize: 15, lineHeight: 1.7, color: T.inkSoft }}>
              {inlineFormat(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Table (lines starting with |)
    if (line.startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse header, separator, body rows
      if (tableLines.length >= 2) {
        const parseRow = (row: string) =>
          row.split("|").slice(1, -1).map((c) => c.trim());
        const headers = parseRow(tableLines[0]);
        // tableLines[1] is the separator (---|---) — skip it
        const rows = tableLines.slice(2).map(parseRow);
        elements.push(
          <div key={key++} style={{ overflowX: "auto", marginBottom: 20 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th
                      key={hi}
                      style={{
                        padding: "8px 12px",
                        textAlign: "left",
                        fontWeight: 700,
                        color: T.ink,
                        backgroundColor: T.paper2,
                        borderBottom: `2px solid ${T.rule}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {inlineFormat(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr
                    key={ri}
                    style={{ backgroundColor: ri % 2 === 0 ? "#fff" : T.paper }}
                  >
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: "8px 12px",
                          color: T.inkSoft,
                          borderBottom: `1px solid ${T.rule}`,
                        }}
                      >
                        {inlineFormat(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph
    elements.push(
      <p
        key={key++}
        style={{
          fontSize: 15,
          lineHeight: 1.8,
          color: T.inkSoft,
          marginBottom: 16,
        }}
      >
        {inlineFormat(line)}
      </p>
    );
    i++;
  }

  return <>{elements}</>;
}

/** Handle **bold** inline formatting */
function inlineFormat(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} style={{ color: T.ink, fontWeight: 600 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}
